import { useCallback, useEffect, useState } from "react";
import config from "../config/index.js";
import { fetchLendingHealth, fetchLoanByDeposit, fetchLoansByOwner } from "../services/api.js";

/**
 * Returns the loan attached to a single deposit, or null if none open.
 * Sources the data exclusively from the backend per the data-flow spec
 * — the API already includes live-computed accrued interest (see
 * lending.controller.js → getByDeposit), so no chain read is required.
 */
export function useLoan(depositId) {
    const [loan, setLoan] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (depositId == null) {
            setLoan(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const api = await fetchLoanByDeposit(depositId);
            if (api?.loan) {
                setLoan(normalise(api.loan, api.ltvBps));
            } else {
                setLoan(null);
            }
        } catch (err) {
            setError(err?.message || "Failed to fetch loan");
            setLoan(null);
        } finally {
            setLoading(false);
        }
    }, [depositId]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return { loan, isLoading, error, refresh };
}

/** Lists every loan for the owner, optionally including closed ones. */
export function useLoansByOwner(owner, { includeClosed = false } = {}) {
    const [loans, setLoans] = useState([]);
    const [isLoading, setLoading] = useState(Boolean(owner));
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!owner) {
            setLoans([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await fetchLoansByOwner(owner, { includeClosed });
            setLoans((data?.items || []).map((l) => normalise(l, l.ltvBps)));
        } catch (err) {
            setError(err?.message || "Failed to fetch loans");
            setLoans([]);
        } finally {
            setLoading(false);
        }
    }, [owner, includeClosed]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Background poll every 15s so a freshly opened / repaid loan shows
    // up across every page that consumes this hook (My Loans list, Loan
    // Detail, dashboard widgets) without waiting for the user to reload
    // — the live indexer can be a few seconds behind the action-flow's
    // sync POST, and the page itself isn't always the one that fired
    // the action.
    useEffect(() => {
        if (!owner) return undefined;
        const id = setInterval(() => {
            refresh();
        }, 15_000);
        return () => clearInterval(id);
    }, [owner, refresh]);

    return { loans, isLoading, error, refresh };
}

/** Aggregate lending health for the protocol dashboards. */
export function useLendingHealth({ pollMs = 60_000 } = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const health = await fetchLendingHealth();
            setData({
                openLoans: Number(health.openLoans || 0),
                totalOutstandingPrincipal: BigInt(health.totalOutstandingPrincipal || "0"),
                totalAccruedInterest: BigInt(health.totalAccruedInterest || "0"),
                avgLtvBps: Number(health.avgLtvBps || 0)
            });
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        if (!pollMs) return undefined;
        const handle = setInterval(refresh, pollMs);
        return () => clearInterval(handle);
    }, [refresh, pollMs]);

    return { data, isLoading, refresh };
}

function normalise(raw, ltvBps) {
    // `accruedInterest` from the backend is already overlaid with live
    // pending interest (lending.controller.js → pendingInterest). But
    // it's a SNAPSHOT — captured at the moment the API was called, then
    // frozen until the next 15-second poll. UI surfaces that need to
    // tick per-second between polls (Loans Overview headline + table)
    // recompute from `storedAccruedInterest + ticker-driven pending`
    // using `lastAccruedAt + aprBps`, both surfaced below.
    const live = BigInt(raw.accruedInterest || "0");
    const pending = BigInt(raw.pendingInterest || "0");
    const stored = live > pending ? live - pending : 0n;
    return {
        depositId: Number(raw.depositId),
        borrower: String(raw.borrower || "").toLowerCase(),
        principal: BigInt(raw.principal || "0"),
        originalPrincipal: BigInt(raw.originalPrincipal || raw.principal || "0"),
        accruedInterest: live,
        storedAccruedInterest: stored,
        pendingInterest: pending,
        // Fall back to the protocol constant when the backend record's
        // aprBps is 0/missing — without this, liveLoanInterest's early
        // return (aprBps === 0n) would freeze the per-second projection
        // on the page while the repay modal (which always uses the
        // config constant) keeps ticking, making the two surfaces show
        // wildly different accrued figures.
        aprBps: Number(raw.aprBps) || config.protocol.loanAprBps,
        liveDebt: BigInt(raw.liveDebt || (BigInt(raw.principal || "0") + live).toString()),
        totalInterestPaid: BigInt(raw.totalInterestPaid || "0"),
        openedAt: raw.openedAt ? new Date(raw.openedAt).getTime() : null,
        // Fall back to `openedAt` when the backend hasn't recorded a
        // `lastAccruedAt` yet (fresh loan, or a CYR-opened loan whose
        // initial state-changed event hadn't been written by the
        // indexer at the moment of the API read). Without this the
        // per-second `liveLoanInterest` projection short-circuits to
        // `stored` and the headline "Loan Interest Cost" card freezes
        // at $0 even though the loan IS accruing on chain.
        lastAccruedAt: raw.lastAccruedAt
            ? new Date(raw.lastAccruedAt).getTime()
            : raw.openedAt
            ? new Date(raw.openedAt).getTime()
            : null,
        closedAt: raw.closedAt ? new Date(raw.closedAt).getTime() : null,
        liquidatedAt: raw.liquidatedAt ? new Date(raw.liquidatedAt).getTime() : null,
        isOpen: raw.isOpen !== false,
        ltvBps: Number(ltvBps || raw.ltvBps || 0)
    };
}
